import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PureQueryOptions, Reference } from '@apollo/client';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useStudentConversationsListQuery,
  useGroupConversationsListQuery,
  useTeacherConversationsListQuery,
  usePostSlackReplyMutation,
  usePostGmailReplyMutation,
  useMarkConvReadMutation,
  useSetConvStatusMutation,
  StudentConversationsListDocument,
  GroupConversationsListDocument,
  TeacherConversationsListDocument,
  ConversationThreadPageDocument,
  ConversationThreadPageQuery,
  SetConvStatusMutation,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

export type ConversationStatus = 'open' | 'in_progress' | 'done';

export interface IConversation {
  id: string;
  kind: string;
  title: string;
  /** Human dialog title (channel name / mail subject / counterpart name). */
  displayTitle: string;
  externalId: string;
  /** Slack workspace team id — powers the «open in Slack» deep link. */
  teamId: string;
  mailboxEmail: string | null;
  unreadCount: number;
  status: string;
  isUnanswered: boolean;
  lastMessageAt: string | null;
}

interface IArgs {
  studentId?: string;
  groupId?: string;
  teacherId?: string;
}

interface IUseConversations {
  conversations: IConversation[];
  loading: boolean;
  replying: boolean;
  reply: (
    conversationId: string,
    text: string,
    kind: string,
  ) => Promise<boolean>;
  /** Mark every message of a conversation read/unread (team-wide) + refetch. */
  markRead: (conversationId: string, read?: boolean) => Promise<void>;
  /** Set the handling status (open / in_progress / done) + refetch. */
  setStatus: (
    conversationId: string,
    status: ConversationStatus,
  ) => Promise<void>;
  /**
   * Refetch options for the conversation list — hand these to a sibling
   * mutation (e.g. create-task-from-message) so the list refreshes too.
   */
  listRefetch: PureQueryOptions | null;
}

const GMAIL_KIND = 'gmail_thread';

type ErrList =
  | Array<{ message?: string | null; code?: string | null } | null>
  | null
  | undefined;

const errText = (errors: ErrList): string | undefined =>
  errors
    ?.map((e) => e?.message || e?.code || null)
    .filter(Boolean)
    .join('\n') || undefined;

/**
 * Slack/Gmail conversations for a student, a group OR a teacher + reply
 * (spec 0003 Phase 3; teacher branch added in spec 0007).
 *
 * Triage mutations (markRead / setStatus) are cache-first (spec 0008): the
 * mutation payload carries the changed conversation, Apollo merges it into
 * the normalized entity, and every list/badge that selects those fields
 * re-renders from the cache — nothing blocks on a refetch. The 60s badge
 * poll remains the cross-client convergence safety net.
 */
export const useConversations = ({
  studentId,
  groupId,
  teacherId,
}: IArgs): IUseConversations => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  // errorPolicy:'all' so a PermissionDenied (e.g. a non-management role opening
  // a card) degrades to an empty panel instead of an unhandled query error.
  const studentQuery = useStudentConversationsListQuery({
    variables: { studentId: studentId || '' },
    skip: !studentId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const groupQuery = useGroupConversationsListQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const teacherQuery = useTeacherConversationsListQuery({
    variables: { teacherId: teacherId || '' },
    skip: !teacherId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const listRefetch = useMemo(() => {
    if (studentId) {
      return {
        query: StudentConversationsListDocument,
        variables: { studentId },
      };
    }
    if (groupId) {
      return { query: GroupConversationsListDocument, variables: { groupId } };
    }
    if (teacherId) {
      return {
        query: TeacherConversationsListDocument,
        variables: { teacherId },
      };
    }
    return null;
  }, [studentId, groupId, teacherId]);

  const [slackMut, slackState] = usePostSlackReplyMutation();
  const [gmailMut, gmailState] = usePostGmailReplyMutation();
  const [markConvRead] = useMarkConvReadMutation();
  const [setConvStatus] = useSetConvStatusMutation();

  const markRead = useCallback(
    async (conversationId: string, read = true) => {
      try {
        await markConvRead({
          variables: { conversationId, read },
          // No refetch: the payload's conversation { unreadCount,
          // isUnanswered } merges into the entity and updates every list
          // and badge. The update() flips the open thread's per-message
          // read flags in the cache so unread styling clears too.
          update: (cache) => {
            // relayStylePagination keys the thread by conversationId only,
            // so first/after are irrelevant to the cache lookup.
            const cached = cache.readQuery<ConversationThreadPageQuery>({
              query: ConversationThreadPageDocument,
              variables: { conversationId },
            });
            (cached?.conversationThread?.edges || []).forEach((edge) => {
              const node = edge?.node;
              if (!node) return;
              cache.modify({
                id: cache.identify({ __typename: 'MessageType', id: node.id }),
                fields: { isRead: () => read },
              });
            });
          },
        });
      } catch {
        // Non-critical: a failed read-marker shouldn't disrupt reading.
      }
    },
    [markConvRead],
  );

  const setStatus = useCallback(
    async (conversationId: string, status: ConversationStatus) => {
      try {
        await setConvStatus({
          variables: { conversationId, status },
          // The status paints instantly; Apollo rolls the write back
          // automatically if the mutation errors.
          optimisticResponse: {
            setConversationStatus: {
              __typename: 'SetConversationStatusMutation',
              success: true,
              customErrors: null,
              conversation: {
                __typename: 'ConversationType',
                id: conversationId,
                status,
              },
            },
          } as SetConvStatusMutation,
          update: (cache) => {
            if (status !== 'done') return;
            // Closing a dialog drops it out of every cached «ждут ответа»
            // page (the dashboard queue queries unansweredOnly:true) —
            // without waiting for that query to refetch.
            cache.modify({
              fields: {
                conversationsBrowser(
                  existing: {
                    edges?: Array<{ node?: Reference | null } | null> | null;
                    totalCount?: number | null;
                  },
                  { storeFieldName, readField },
                ) {
                  if (!storeFieldName.includes('"unansweredOnly":true')) {
                    return existing;
                  }
                  const edges = (existing?.edges || []).filter(
                    (edge) =>
                      String(readField('id', edge?.node || undefined)) !==
                      conversationId,
                  );
                  if (edges.length === (existing?.edges || []).length) {
                    return existing;
                  }
                  return {
                    ...existing,
                    edges,
                    totalCount: Math.max(0, (existing?.totalCount || 1) - 1),
                  };
                },
              },
            });
          },
        });
      } catch {
        // Non-critical.
      }
    },
    [setConvStatus],
  );

  const reply = useCallback(
    async (conversationId: string, text: string, kind: string) => {
      const isGmail = kind === GMAIL_KIND;
      const opts = {
        variables: { conversationId, text },
        // The send button unblocks on the mutation itself; the thread and
        // the conversation list (isUnanswered flips) refresh in the
        // background — no awaitRefetchQueries (spec 0008).
        refetchQueries: [
          {
            query: ConversationThreadPageDocument,
            variables: { conversationId, first: 30 },
          },
          ...(listRefetch ? [listRefetch] : []),
        ],
      };
      try {
        const p = isGmail
          ? (await gmailMut(opts)).data?.sendGmailReply
          : (await slackMut(opts)).data?.sendSlackMessage;
        if (p?.success) return true;
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.failed'),
            text: errText(p?.customErrors),
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return false;
      } catch (e) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.communications.failed'),
            text: (e as Error).message,
          },
          type: GlobalToastTypeNames.FakturowniaMatch,
        });
        return false;
      }
    },
    [slackMut, gmailMut, listRefetch, addToast, t],
  );

  let rawConversations: IConversation[] | undefined;
  if (studentId) {
    rawConversations = studentQuery.data?.studentConversations as
      | IConversation[]
      | undefined;
  } else if (teacherId) {
    rawConversations = teacherQuery.data?.teacherConversations as
      | IConversation[]
      | undefined;
  } else {
    rawConversations = groupQuery.data?.groupConversations as
      | IConversation[]
      | undefined;
  }

  // Backend resolves displayTitle for every kind; the || chain only guards
  // against a stale cache entry fetched before the field existed.
  const conversations = useMemo(
    () =>
      (rawConversations || []).map((c) => ({
        ...c,
        displayTitle: c.displayTitle || c.title || c.externalId,
        teamId: c.teamId || '',
      })),
    [rawConversations],
  );

  let listLoading = groupQuery.loading;
  if (studentId) {
    listLoading = studentQuery.loading;
  } else if (teacherId) {
    listLoading = teacherQuery.loading;
  }

  return {
    conversations,
    loading: listLoading,
    replying: slackState.loading || gmailState.loading,
    reply,
    markRead,
    setStatus,
    listRefetch,
  };
};
